import { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import styles from "./style.module.scss";
import { Menu, X, ChevronDown, UserCircle, LogOut, LayoutDashboard } from "lucide-react";
import TranslateSelect from "../../Components/TranslateSelect";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../utils/AuthContext";

const Header = () => {
  const { t } = useTranslation("header");
  const { t: tAuth } = useTranslation("auth");
  const data = t("data", { returnObjects: true });
  const navigate = useNavigate();
  const pressTimer = useRef(null);
  const { user, isAuthenticated, logout } = useAuth();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth <= 1050;
      setIsMobile(isMobileView);
      if (!isMobileView) {
        setIsMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleLogoPressStart = (e) => {
    // Only trigger for touch or main mouse button
    if (e.button !== undefined && e.button !== 0) return;
    
    pressTimer.current = setTimeout(() => {
      navigate('/admin/login');
    }, 3000); // 3 seconds long-press
  };

  const handleLogoPressEnd = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const toggleSubmenu = (id) => {
    if (isMobile) {
      setOpenSubmenu(openSubmenu === id ? null : id);
    }
  };

  const renderNavLinks = (lists) =>
    lists.map(({ name, to: path, id, subTours }) => {
      const hasSubTours = subTours && subTours.length > 0;

      return (
        <li
          key={id}
          className={`${styles.navItem} ${hasSubTours ? styles.hasDropdown : ""} ${openSubmenu === id ? styles.submenuOpen : ""}`}
        >
          {path ? (
            <NavLink
              to={path}
              className={({ isActive }) =>
                `${styles.link} ${isActive ? styles.active : ""}`
              }
              onClick={() => setIsMenuOpen(false)}
            >
              {name}
            </NavLink>
          ) : (
            <div className={styles.link} onClick={() => toggleSubmenu(id)}>
              {name}
              {hasSubTours && (
                <ChevronDown size={14} className={styles.chevron} />
              )}
            </div>
          )}

          {hasSubTours && (
            <AnimatePresence>
              {isMobile ? (
                openSubmenu === id && (
                  <motion.ul 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className={styles.dropdown}
                  >
                    {subTours.map((subTour) => (
                      <li key={subTour.id}>
                        <NavLink
                          to={subTour.to}
                          className={({ isActive }) =>
                            `${styles.subLink} ${isActive ? styles.active : ""}`
                          }
                          onClick={() => {
                            setIsMenuOpen(false);
                            setOpenSubmenu(null);
                          }}
                        >
                          {subTour.name}
                        </NavLink>
                      </li>
                    ))}
                  </motion.ul>
                )
              ) : (
                <ul className={styles.dropdown}>
                  {subTours.map((subTour) => (
                    <li key={subTour.id}>
                      <NavLink
                        to={subTour.to}
                        className={({ isActive }) =>
                          `${styles.subLink} ${isActive ? styles.active : ""}`
                        }
                        onClick={() => {
                          setIsMenuOpen(false);
                          setOpenSubmenu(null);
                        }}
                      >
                        {subTour.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </AnimatePresence>
          )}
        </li>
      );
    });

  return (
    <header className={styles.headerBox}>
      <div className={`${styles.header} ${scrolled ? styles.active : ""}`}>
        <NavLink 
          className={styles.logoCont} 
          to="/"
          onPointerDown={handleLogoPressStart}
          onPointerUp={handleLogoPressEnd}
          onPointerLeave={handleLogoPressEnd}
        >
          <img src="/assets/icons/logo.png" alt="Logo" />
        </NavLink>
        <AnimatePresence>
          {(!isMobile || isMenuOpen) && (
            <motion.nav
              initial={isMobile ? { x: "100%", opacity: 0 } : { opacity: 0 }}
              animate={isMobile ? { x: 0, opacity: 1 } : { opacity: 1 }}
              exit={isMobile ? { x: "100%", opacity: 0 } : { opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className={`${styles.navigation} ${isMenuOpen ? styles.active : ""}`}
            >
              <ul className={styles.linkLists}>
                {renderNavLinks(isMobile ? data.pageList2 : data.pagesList)}
              </ul>
            </motion.nav>
          )}
        </AnimatePresence>
        <TranslateSelect />

        {/* User Auth Section */}
        {isAuthenticated ? (
          <div className={styles.userAuthSection}>
            <button
              className={styles.userAvatarBtn}
              onClick={() => setShowUserMenu(!showUserMenu)}
              id="user-menu-toggle"
            >
              <span className={styles.avatarCircle}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </span>
            </button>
            <AnimatePresence>
              {showUserMenu && (
                <motion.div
                  className={styles.userDropdown}
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className={styles.dropdownHeader}>
                    <span className={styles.dropdownName}>{user?.firstName} {user?.lastName}</span>
                    <span className={styles.dropdownEmail}>{user?.email}</span>
                  </div>
                  <div className={styles.dropdownDivider} />
                  <button onClick={() => { navigate('/dashboard'); setShowUserMenu(false); }} className={styles.dropdownItem}>
                    <LayoutDashboard size={15} />
                    {tAuth('dashboard')}
                  </button>
                  <button onClick={() => { logout(); setShowUserMenu(false); navigate('/'); }} className={styles.dropdownItemDanger}>
                    <LogOut size={15} />
                    {tAuth('signOut')}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <NavLink to="/auth/login" className={styles.signInBtn} id="header-sign-in">
            <UserCircle size={18} />
            <span>{tAuth('signIn')}</span>
          </NavLink>
        )}

        <a
          className={styles.whatsUpCont}
          target="_blank"
          href="https://wa.me/37477271066"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7 .9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
          </svg>
          WhatsApp
        </a>

        {isMobile && (
          <div
            className={styles.menuIcon}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
